import { useEffect, useRef } from "react";

const SIZE = 120;
const RADIUS = 46;

function parseCssColor(value) {
    const hex = String(value || "").trim();
    const hexMatch = hex.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
    if (hexMatch) {
        let h = hexMatch[1];
        if (h.length === 3) h = h.split("").map((c) => c + c).join("");
        return {
            r: parseInt(h.slice(0, 2), 16),
            g: parseInt(h.slice(2, 4), 16),
            b: parseInt(h.slice(4, 6), 16),
        };
    }
    const rgbMatch = hex.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
    if (rgbMatch) {
        return { r: Number(rgbMatch[1]), g: Number(rgbMatch[2]), b: Number(rgbMatch[3]) };
    }
    return { r: 5, g: 34, b: 122 };
}

function rgbToHsl({ r, g, b }) {
    r /= 255;
    g /= 255;
    b /= 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    if (max === min) return { h: 0, s: 0, l };
    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let h = 0;
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    return { h: h / 6, s, l };
}

function hslToRgb({ h, s, l }) {
    if (s === 0) {
        const v = Math.round(l * 255);
        return { r: v, g: v, b: v };
    }
    const hue2rgb = (p, q, t) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    return {
        r: Math.round(hue2rgb(p, q, h + 1 / 3) * 255),
        g: Math.round(hue2rgb(p, q, h) * 255),
        b: Math.round(hue2rgb(p, q, h - 1 / 3) * 255),
    };
}

function rgba({ r, g, b }, a) {
    return `rgba(${r}, ${g}, ${b}, ${a})`;
}

function lift(hsl, lightness, saturationBoost = 0) {
    return hslToRgb({
        h: hsl.h,
        s: Math.min(1, hsl.s + saturationBoost),
        l: lightness,
    });
}

function fibonacciSphere(count) {
    const points = [];
    const golden = Math.PI * (3 - Math.sqrt(5));
    for (let i = 0; i < count; i += 1) {
        const y = 1 - (i / (count - 1)) * 2;
        const r = Math.sqrt(Math.max(0, 1 - y * y));
        const theta = golden * i;
        points.push({
            x: Math.cos(theta) * r,
            y,
            z: Math.sin(theta) * r,
        });
    }
    return points;
}

function makeRing(y, count) {
    const r = Math.sqrt(Math.max(0, 1 - y * y));
    const points = [];
    for (let i = 0; i < count; i += 1) {
        const a = (i / count) * Math.PI * 2;
        points.push({
            x: Math.cos(a) * r,
            y,
            z: Math.sin(a) * r,
        });
    }
    return points;
}

function makeMeridian(lon, count) {
    const points = [];
    for (let i = 0; i <= count; i += 1) {
        const lat = (i / count) * Math.PI - Math.PI / 2;
        const cr = Math.cos(lat);
        points.push({
            x: cr * Math.cos(lon),
            y: Math.sin(lat),
            z: cr * Math.sin(lon),
        });
    }
    return points;
}

function dist3(a, b) {
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    const dz = a.z - b.z;
    return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

function connectNearby(points, maxDist) {
    const edges = [];
    for (let i = 0; i < points.length; i += 1) {
        for (let j = i + 1; j < points.length; j += 1) {
            if (dist3(points[i], points[j]) < maxDist) {
                edges.push([i, j]);
            }
        }
    }
    return edges;
}

function rotatePoint(p, rotY, rotX) {
    const cy = Math.cos(rotY);
    const sy = Math.sin(rotY);
    const x1 = p.x * cy - p.z * sy;
    const z1 = p.x * sy + p.z * cy;
    const cx = Math.cos(rotX);
    const sx = Math.sin(rotX);
    return {
        x: x1,
        y: p.y * cx - z1 * sx,
        z: p.y * sx + z1 * cx,
    };
}

function project(p, rotY, rotX) {
    const r = rotatePoint(p, rotY, rotX);
    const perspective = 2.15 / (2.15 + r.z);
    return {
        x: SIZE / 2 + r.x * RADIUS * perspective,
        y: SIZE / 2 + r.y * RADIUS * perspective,
        z: r.z,
        s: perspective,
    };
}

function readBrandPalette() {
    const raw = getComputedStyle(document.documentElement).getPropertyValue("--brand-color");
    const brand = parseCssColor(raw);
    const hsl = rgbToHsl(brand);
    return {
        brand,
        line: lift(hsl, 0.56, 0.08),
        node: lift(hsl, 0.74, 0.12),
        core: lift(hsl, 0.9, 0.15),
        halo: lift(hsl, 0.42, 0.05),
        back: hslToRgb({ h: hsl.h, s: Math.min(1, hsl.s + 0.1), l: 0.04 }),
    };
}

export default function PatientAiOrb() {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return undefined;

        const ctx = canvas.getContext("2d");
        if (!ctx) return undefined;

        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        canvas.width = SIZE * dpr;
        canvas.height = SIZE * dpr;
        ctx.scale(dpr, dpr);

        const palette = readBrandPalette();
        const shell = fibonacciSphere(86);
        const shellEdges = connectNearby(shell, 0.52);
        const mid = fibonacciSphere(36).map((p) => ({
            x: p.x * 0.52,
            y: p.y * 0.52,
            z: p.z * 0.52,
        }));
        const coreSeed = fibonacciSphere(28);
        const core = coreSeed.map((p, i) => {
            const k = 0.14 + (i % 5) * 0.018;
            return { x: p.x * k, y: p.y * k, z: p.z * k };
        });
        const rings = [-0.72, -0.38, 0, 0.38, 0.72].map((y) => makeRing(y, 48));
        const meridians = [0, 0.45, 0.9, 1.35, 1.8, 2.25].map((lon) => makeMeridian(lon, 36));
        const spikeIndex = shell
            .map((_, i) => i)
            .filter((i) => i % 4 === 0);

        let rotY = 0.4;
        let rotX = 0.28;
        let time = 0;
        let frameId = 0;
        const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

        const drawPolyline = (points, rotYNow, rotXNow, alpha) => {
            const projected = points.map((p) => project(p, rotYNow, rotXNow));
            ctx.beginPath();
            projected.forEach((p, i) => {
                if (i === 0) ctx.moveTo(p.x, p.y);
                else ctx.lineTo(p.x, p.y);
            });
            ctx.strokeStyle = rgba(palette.line, alpha);
            ctx.lineWidth = 0.55;
            ctx.stroke();
        };

        const drawClosedRing = (points, rotYNow, rotXNow, alpha) => {
            const projected = points.map((p) => project(p, rotYNow, rotXNow));
            ctx.beginPath();
            projected.forEach((p, i) => {
                if (i === 0) ctx.moveTo(p.x, p.y);
                else ctx.lineTo(p.x, p.y);
            });
            ctx.closePath();
            ctx.strokeStyle = rgba(palette.line, alpha);
            ctx.lineWidth = 0.5;
            ctx.setLineDash([2.2, 2.6]);
            ctx.stroke();
            ctx.setLineDash([]);
        };

        const draw = () => {
            ctx.clearRect(0, 0, SIZE, SIZE);

            ctx.beginPath();
            ctx.arc(SIZE / 2, SIZE / 2, SIZE / 2, 0, Math.PI * 2);
            ctx.fillStyle = rgba(palette.back, 1);
            ctx.fill();

            const pulse = 0.88 + Math.sin(time * 1.6) * 0.12;
            const glow = ctx.createRadialGradient(
                SIZE / 2,
                SIZE / 2,
                2,
                SIZE / 2,
                SIZE / 2,
                34 * pulse
            );
            glow.addColorStop(0, rgba(palette.core, 0.95));
            glow.addColorStop(0.22, rgba(palette.node, 0.55));
            glow.addColorStop(0.5, rgba(palette.halo, 0.28));
            glow.addColorStop(1, rgba(palette.brand, 0));
            ctx.fillStyle = glow;
            ctx.beginPath();
            ctx.arc(SIZE / 2, SIZE / 2, 34 * pulse, 0, Math.PI * 2);
            ctx.fill();

            rings.forEach((ring) => drawClosedRing(ring, rotY, rotX, 0.22));
            meridians.forEach((line) => drawPolyline(line, rotY, rotX, 0.16));

            const shellP = shell.map((p) => project(p, rotY, rotX));
            shellEdges.forEach(([i, j]) => {
                const a = shellP[i];
                const b = shellP[j];
                const depth = (a.z + b.z) * 0.5;
                if (depth < -0.55) return;
                const alpha = (0.16 + (depth + 1) * 0.18) * ((a.s + b.s) * 0.5);
                ctx.beginPath();
                ctx.moveTo(a.x, a.y);
                ctx.lineTo(b.x, b.y);
                ctx.strokeStyle = rgba(palette.line, alpha);
                ctx.lineWidth = 0.65;
                ctx.stroke();
            });

            spikeIndex.forEach((i) => {
                const origin = shell[i];
                const tip = {
                    x: origin.x * 1.16,
                    y: origin.y * 1.16,
                    z: origin.z * 1.16,
                };
                const a = project(origin, rotY, rotX);
                const b = project(tip, rotY, rotX);
                if (a.z < 0.05) return;
                ctx.beginPath();
                ctx.moveTo(a.x, a.y);
                ctx.lineTo(b.x, b.y);
                ctx.strokeStyle = rgba(palette.node, 0.28 * a.s);
                ctx.lineWidth = 0.7;
                ctx.stroke();
            });

            const drawDots = (points, baseR, alphaScale) => {
                points.forEach((p) => {
                    const q = project(p, rotY, rotX);
                    const r = baseR * q.s;
                    ctx.beginPath();
                    ctx.arc(q.x, q.y, r, 0, Math.PI * 2);
                    ctx.fillStyle = rgba(palette.node, (0.35 + 0.5 * q.s) * alphaScale);
                    ctx.fill();
                });
            };

            drawDots(shell, 1.15, 0.85);
            drawDots(mid, 1.05, 0.7);
            drawDots(core, 1.35, 1);

            const coreGlow = ctx.createRadialGradient(
                SIZE / 2,
                SIZE / 2,
                0,
                SIZE / 2,
                SIZE / 2,
                10 * pulse
            );
            coreGlow.addColorStop(0, "rgba(255, 255, 255, 0.92)");
            coreGlow.addColorStop(0.45, rgba(palette.core, 0.7));
            coreGlow.addColorStop(1, rgba(palette.halo, 0));
            ctx.fillStyle = coreGlow;
            ctx.beginPath();
            ctx.arc(SIZE / 2, SIZE / 2, 10 * pulse, 0, Math.PI * 2);
            ctx.fill();

            if (!reduceMotion) {
                rotY += 0.007;
                rotX = 0.28 + Math.sin(time * 0.35) * 0.08;
                time += 0.016;
                frameId = requestAnimationFrame(draw);
            }
        };

        const onVisibility = () => {
            if (document.hidden) {
                cancelAnimationFrame(frameId);
                return;
            }
            if (!reduceMotion) {
                cancelAnimationFrame(frameId);
                frameId = requestAnimationFrame(draw);
            }
        };

        draw();
        document.addEventListener("visibilitychange", onVisibility);

        return () => {
            cancelAnimationFrame(frameId);
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, []);

    return <canvas ref={canvasRef} className="patient-ai-orb" width={SIZE} height={SIZE} />;
}
